import React, { useEffect, useState } from 'react';

import { useApp } from 'contexts/AppContext';
import { useUser } from 'contexts/UserContext';

import moment from 'utils/moment';
import {
  ACTIVATION,
  FUNNEL,
  GROUP,
  PROPERTIES,
  SCHEMA,
  YOUGOVE_SEGMENTS,
  YOUGOVMETRICS
} from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Switch, Select } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import Loading from 'components/Loading';
import { SearchField } from 'components/SearchField';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalForm } from '../../../components/funnel/ModalForm';
import { ModalMail } from '../../../components/funnel/ModalMail';
import { ModalDelete } from '../../../components/ModalDelete';

const FunnelContainer = () => {
  const { searchFieldValue, setSearchFieldValue } = useApp();
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [init, setInit] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [openModal, setModal] = useState(null);
  const [status, setStatus] = useState('false');
  const [selectedFunnel, setSelectedFunnel] = useState({});
  const [config, setConfig] = useState({});
  const { adminRights } = useUser();

  const closeModal = () => {
    setModal(null);
    setSelectedFunnel({});
  };

  const seachKey = (item, toTest) => {
    const val = toTest || searchFieldValue.toLowerCase();
    for (const key in item) {
      if (
        typeof item[key] === 'string' &&
        item[key].toLowerCase().indexOf(val) !== -1
      ) {
        return true;
      }
    }
    return false;
  };

  const selectFunnel = async id => {
    const funnel = await FUNNEL.getFunnel(id);
    setSelectedFunnel(funnel);
    return funnel;
  };

  const findPropertie = (id, properties) => {
    for (const propertie of properties) {
      for (const funnelId of propertie.funnelIds) {
        if (funnelId === id) {
          return propertie.name;
        }
      }
    }
    return 'Autre';
  };

  const getDatas = async () => {
    const { properties, users } = config;
    const resp = await FUNNEL.getFunnels(
      ['name', 'userId', 'lastUpdate', 'schemaId', 'measureName', 'archived'],
      status
    );

    const mapped = resp.funnels.map(item => {
      const newItem = item;
      newItem.email = item.userId
        ? users.find(el => el._id === item.userId)?.email || 'Autre domain'
        : 'Aucun';
      newItem.measure = item.schemaId ? item.schemaId.key : item.measureName;
      newItem.propertie = findPropertie(item._id, properties);
      newItem.lastUpdate = moment(item.lastUpdate).format('DD/MM/YYYY');
      return newItem;
    });

    setAllRows(mapped);
    setRefetch(false);
    // eslint-disable-next-line
  };

  const initDatas = async () => {
    const [
      properties,
      funnelConfig,
      yougovMetrics,
      yougovSegments,
      schemaFunnel,
      presetActivation
    ] = await Promise.all([
      PROPERTIES.get(),
      FUNNEL.getConfig(),
      YOUGOVMETRICS.getYougovMetrics(),
      YOUGOVE_SEGMENTS.getAll(),
      SCHEMA.get(),
      ACTIVATION.getActivationPresets()
    ]);

    funnelConfig.yougovMetrics = yougovMetrics;
    funnelConfig.yougovSegments = yougovSegments;
    funnelConfig.schemaFunnel = schemaFunnel;
    funnelConfig.presetActivation = presetActivation;
    setConfig({ ...funnelConfig, properties });
    setInit(true);
    setRefetch(true);
  };

  useEffect(() => {
    setRows(
      !searchFieldValue
        ? allRows
        : allRows.filter(item => {
            return seachKey(item, searchFieldValue);
          })
    );
    // eslint-disable-next-line
  }, [allRows]);

  useEffect(() => {
    if (!init) {
      initDatas();
    }
    // eslint-disable-next-line
  }, [init]);

  useEffect(() => {
    if (refetch && init) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [refetch]);

  useEffect(() => {
    if (!refetch && init) {
      setRefetch(true);
    }
    // eslint-disable-next-line
  }, [status]);

  const openCreateModal = () => {
    if (adminRights?.funnel?.funnel?.create) {
      setModal('ModalCreate');
    } else {
      setModal('unauthorized');
    }
  };
  const onCreate = async ({ tagMapping, propertie, group, ...datas }) => {
    const { _id: funnelId } = await FUNNEL.create(datas);
    if (tagMapping.tag && tagMapping.correspondences.length !== 0) {
      await FUNNEL.setTagMapping({
        tagMapping: tagMapping.correspondences.map(e => ({
          customLabel: e.tagLevel.label,
          funnelId,
          funnelStep: e.funnelLevel,
          tagFunnelId: tagMapping.tag._id,
          tagFunnelLevel: e.tagLevel.level
        }))
      });
    }
    for await (const item of propertie) {
      await PROPERTIES.addFunnel({ propertieId: item._id, funnelId });
    }
    for await (const item of group) {
      await GROUP.addFunnel({ groupId: item._id, funnelId });
    }
    setRefetch(true);
    closeModal();
  };

  const openUpdateModal = async id => {
    if (adminRights?.funnel?.funnel?.update) {
      const season = await FUNNEL.getSeasonality(id);
      const funnel = await FUNNEL.getFunnelById(id);
      const funnelTagMapping = await FUNNEL.getTagMapping(id);
      const seasonality = (season && season[0] && season[0].seasonality) || {};

      setSelectedFunnel({
        ...funnel,
        seasonality,
        funnelTagMapping
      });
      setModal('modalUpdate');
    } else {
      setModal('unauthorized');
    }
  };

  const onUpdate = async ({
    tagMapping,
    seasonality,
    group,
    propertie,
    ...datas
  }) => {
    try {
      const funnelId = selectedFunnel._id;
      const { groupes, properties } = config;

      /* manage groups */
      for await (const item of groupes) {
        const funnelInGroup =
          item.funnelIds.filter(funnel => funnel._id === funnelId).length !== 0;
        if (group.find(el => el.name === item.name)) {
          if (!funnelInGroup) {
            await GROUP.addFunnel({ groupId: item._id, funnelId });
          }
        } else if (funnelInGroup) {
          await GROUP.deleteFunnel({ groupId: item._id, funnelId });
        }
      }

      /* manage properties */
      for await (const item of properties) {
        const inProperties = item.funnelIds.indexOf(funnelId) !== -1;
        if (propertie.find(el => el._id === item._id)) {
          if (!inProperties) {
            await PROPERTIES.addFunnel({
              propertieId: item._id,
              funnelId
            });
          }
        } else if (inProperties) {
          await PROPERTIES.deleteFunnel({ propertieId: item._id, funnelId });
        }
      }

      /* manage funnelTagMapping to remove */
      await Promise.all(
        selectedFunnel.funnelTagMapping
          .filter(
            el => !tagMapping.correspondences.find(tag => tag._id === el._id)
          )
          .map(async el => FUNNEL.deleteTagMapping(el._id))
      );

      if (seasonality) {
        await FUNNEL.setSeasonality({ funnelId, seasonality });
      }

      await FUNNEL.update(funnelId, datas);
      if (tagMapping.tag && tagMapping.correspondences.length !== 0) {
        await FUNNEL.setTagMapping({
          tagMapping: tagMapping.correspondences.map(e => ({
            _id: e._id || null,
            customLabel: e.tagLevel.label,
            funnelId,
            funnelStep: e.funnelLevel,
            tagFunnelId: tagMapping.tag._id,
            tagFunnelLevel: e.tagLevel.level
          }))
        });
      }
    } catch (e) {
      console.error(e);
    }

    setRefetch(true);
    closeModal();
  };

  const openDeleteModal = id => {
    if (adminRights?.funnel?.funnel?.delete) {
      selectFunnel(id);
      setModal('modalDelete');
    } else {
      setModal('unauthorized');
    }
  };

  const onDelete = async () => {
    await FUNNEL.deleteFunnel(selectedFunnel._id);
    setRefetch(true);
    closeModal();
  };

  const sendMail = funnel => {
    FUNNEL.sendMailValidation(funnel._id);
    closeModal();
  };

  const openModalSendMail = id => {
    selectFunnel(id);
    setModal('ModalMail');
  };

  const handleArchived = async (id, value) => {
    await FUNNEL.update(id, { archived: value });
    setAllRows(prev => {
      const items = [...prev];
      const item = items.find(el => el.id === id);
      item.archived = value;
      return items;
    });
  };

  return (
    <Container>
      <Row spacing={4}>
        <Item xs={3}>
          <Select
            disabled={refetch}
            title="Filtrer par statut"
            value={status}
            onChange={value => setStatus(value)}
            options={[
              { label: 'Tous les funnels', value: 'all' },
              { label: 'Actifs', value: 'false' }, // logique inversé car on va regarder la props archived
              { label: 'Inactifs', value: 'true' }
            ]}
          />
        </Item>
        <Item xs={3}>
          <SearchField
            onChange={newRows => setRows(newRows)}
            datas={allRows}
            inComponent
            titleHead="Recherche un funnel"
            setSearchFieldValue={setSearchFieldValue}
            placeholder="Funnel id, propriété, nom, ..."
          />
        </Item>
        <Item justify="flex-end" xs>
          <ButtonCreate disabled={!init} onClick={openCreateModal}>
            Ajouter un funnel
          </ButtonCreate>
        </Item>
        <Item>
          <Loading loading={!init || refetch}>
            <TableData
              rows={rows}
              headers={[
                { label: 'Id Funnel', id: '_id', sortKey: '_id' },
                { label: 'Nom', id: 'name', sortKey: 'name' },
                { label: 'Propriété', id: 'propertie', sortKey: 'propertie' },
                {
                  label: 'Propriétaire',
                  id: 'email',
                  style: { color: 'funnel' },
                  sortKey: 'email'
                },
                { label: 'Type', id: 'measure', sortKey: 'measure' },
                {
                  label: 'Dernière MAJ',
                  id: 'lastUpdate',
                  sortKey: 'lastUpdate'
                },
                {
                  label: 'Actif',
                  id: '_id',
                  format: (id, row) => {
                    return (
                      <Switch
                        checked={!row.archived}
                        onChange={async value => {
                          await handleArchived(id, !value);
                        }}
                        iosStyle
                      />
                    );
                  }
                }
              ]}
              onUpdate={openUpdateModal}
              onDelete={openDeleteModal}
              onSendMail={openModalSendMail}
            />
          </Loading>
          {openModal === 'modalUpdate' && (
            <ModalForm
              funnel={selectedFunnel}
              open={openModal === 'modalUpdate'}
              onClose={closeModal}
              onValidate={onUpdate}
              actionName="Modifier le funnel"
              config={config}
            />
          )}
          {openModal === 'modalDelete' && (
            <ModalDelete
              name={selectedFunnel.name}
              type="ce funnel"
              open={openModal === 'modalDelete'}
              onClose={closeModal}
              onDelete={onDelete}
            />
          )}
          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
          {openModal === 'ModalMail' && (
            <ModalMail
              open={openModal === 'ModalMail'}
              onClose={closeModal}
              selectedFunnel={selectedFunnel}
              onSendMail={sendMail}
            />
          )}
          {openModal === 'ModalCreate' && (
            <ModalForm
              open={openModal === 'ModalCreate'}
              onClose={closeModal}
              config={config}
              onValidate={onCreate}
              actionName="Créer le funnel"
            />
          )}
        </Item>
      </Row>
    </Container>
  );
};

export default FunnelContainer;
